/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useState, useEffect, useRef} from 'react'
import PropTypes from 'prop-types'
import {
    Box,
    Container,
    SimpleGrid,
    Stack,
    Alert,
    AlertIcon
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {useForm, Controller} from 'react-hook-form'
import {RadioCardGroup} from '/overrides/app/components/radio-card'
import {MESSAGE_PROPTYPE} from '@salesforce/retail-react-app/app/utils/locale'
import {useShopperCustomersMutation} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-customer'
import {useCurrentBasket} from '@epmc-mach/accelarator/overrides/app/hooks/use-current-basket'
import {useCheckoutAddresses} from '../../checkout-addresses-context'
import ShippingAddressForm from './shipping-address-form'
import AddressCard from '../shared/address-card'
import AddNewAddressButton from '../shared/add-new-address-button'
import {DEFAULT_SHIPPING_VALUES, findMatchingAddress} from '../shared/form-constants'
import {useCustomerLocation} from '@epmc-mach/accelarator/overrides/app/contexts/CustomerLocation'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useTranslationHelpers} from '@epmc-mach/accelarator/overrides/app/hooks'
import PrivacyConsentText from '../shared/privacy-consent-text'
import Button from '/overrides/app/components/button'

const ShippingAddressSelection = ({
    form,
    selectedAddress,
    submitButtonLabel,
    formTitleAriaLabel,
    onSubmit = async () => null,
    onAddressSelect = () => null
}) => {
    const {tShipping} = useTranslationHelpers()

    const {data: customer, refetch: refetchCustomer} = useCurrentCustomer()
    const {refetch: refetchBasket} = useCurrentBasket()
    const {refreshAddressData} = useCheckoutAddresses()
    const {defaultCountryCode} = useCustomerLocation()

    const customerAddresses = customer?.addresses
    const hasSavedAddresses = customerAddresses?.length > 0
    const [isEditingAddress, setIsEditingAddress] = useState(false)
    const hasUserInteractedRef = useRef(false)
    const [selectedAddressId, setSelectedAddressId] = useState(undefined)
    const [isNewAddress, setIsNewAddress] = useState(false)
    const [submitError, setSubmitError] = useState(false)

    const defaultForm = useForm({
        mode: 'onChange',
        shouldUnregister: false
    })
    if (!form) form = defaultForm

    useEffect(() => {
        if (customer?.isGuest && selectedAddress && Object.keys(selectedAddress).length > 0) {
            form.reset({...selectedAddress})
        }
    }, [])

    const matchedAddress = findMatchingAddress(selectedAddress, customer?.addresses)
    const removeCustomerAddress = useShopperCustomersMutation('removeCustomerAddress')

    useEffect(() => {
        // Don't reset form if user is editing
        if (!isEditingAddress && selectedAddress && customer?.isGuest) {
            form.reset({...selectedAddress})
        }
    }, [selectedAddress, isEditingAddress, customer?.isGuest])

    useEffect(() => {
        if (!hasUserInteractedRef.current) {
            // If user has saved addresses and is NOT a guest
            if (
                !customer?.isGuest &&
                customerAddresses?.length > 0 &&
                isEditingAddress &&
                selectedAddressId === undefined
            ) {
                setIsEditingAddress(false)
            }

            // If the customer deletes all their saved addresses during checkout,
            // we need to make sure to display the address form.
            // Also show address form immediately for guest users
            if ((!customerAddresses?.length && !isEditingAddress) || customer?.isGuest) {
                setIsEditingAddress(true)
            }
        }
    }, [customer?.isGuest, customerAddresses?.length, isEditingAddress, selectedAddressId])

    useEffect(() => {
        if (matchedAddress) {
            form.reset({
                addressId: matchedAddress.addressId,
                ...matchedAddress
            })
            onAddressSelect?.(matchedAddress)
        }

        if (!matchedAddress && selectedAddressId) {
            setIsEditingAddress(true)
        }
    }, [matchedAddress, selectedAddressId])

    // Updates the selected customer address if we've an address selected
    // else saves a new customer address
    const submitForm = async (address) => {
        setSubmitError(false)
        if (selectedAddressId) {
            address = {...address, addressId: selectedAddressId}
        }

        try {
            await onSubmit(address)

            // Only reset form after successful submission
            hasUserInteractedRef.current = false
            setIsNewAddress(false)
            setIsEditingAddress(false)
            form.reset({addressId: ''})
        } catch (error) {
            // Don't reset form on error to preserve user data
        }
    }

    // Acts as our `onChange` handler for addressId radio group. We do this
    // manually here so we can toggle off the 'add address' form as needed.
    const handleAddressIdSelection = (addressId) => {
        if (addressId && isEditingAddress) {
            setIsEditingAddress(false)
        }

        const address = customer?.addresses?.find((addr) => addr.addressId === addressId)

        if (address) {
            form.reset({...address})
            onAddressSelect?.(address)
        }
    }

    const removeSavedAddress = async (addressId) => {
        if (addressId === selectedAddressId) {
            setSelectedAddressId(undefined)
            setIsEditingAddress(false)
            form.reset({addressId: ''})
        }

        await removeCustomerAddress.mutateAsync(
            {
                parameters: {
                    customerId: customer.customerId,
                    addressName: addressId
                }
            },
            {
                onSuccess: () => {
                    // If last address removed show new form again
                    if (customer?.addresses?.length === 1) {
                        setIsEditingAddress(true)
                        form.reset({addressId: ''})
                    }
                }
            }
        )

        await refreshAddressData(refetchCustomer, refetchBasket)
    }

    // Edit existing address
    const handleEditAddress = (address) => {
        hasUserInteractedRef.current = true
        setSelectedAddressId(address.addressId)

        // Handle form reset here
        form.reset({...address})
        setIsEditingAddress(true)
        setIsNewAddress(false)
    }

    // Close address form
    const handleCloseForm = () => {
        hasUserInteractedRef.current = true
        setSelectedAddressId(undefined)
        form.reset({addressId: ''})
        setIsEditingAddress(false)
        setIsNewAddress(false)
    }

    useEffect(() => {
        if (isEditingAddress && selectedAddressId === undefined && isNewAddress) {
            form.reset({
                ...DEFAULT_SHIPPING_VALUES,
                countryCode: defaultCountryCode
            })
        }
    }, [isEditingAddress, selectedAddressId, isNewAddress])

    // Open new address form
    const handleNewAddress = () => {
        hasUserInteractedRef.current = true
        setSelectedAddressId(undefined)
        setIsEditingAddress(true)
        setIsNewAddress(true)
    }

    const handleError = () => {
        setSubmitError(true)
    }

    return (
        <form onSubmit={form.handleSubmit(submitForm, handleError)}>
            {submitError && (
                <Alert status="error" mb={2}>
                    <AlertIcon />
                    {tShipping?.addressNotSelected}
                </Alert>
            )}
            <Stack spacing={4}>
                {hasSavedAddresses && (
                    <Controller
                        name="addressId"
                        defaultValue=""
                        control={form.control}
                        rules={{required: !isEditingAddress}}
                        render={({field: {value}}) => (
                            <RadioCardGroup
                                value={value}
                                onChange={handleAddressIdSelection}
                                name="shippingAddressId"
                            >
                                <SimpleGrid
                                    columns={[1, 1, 2]}
                                    spacing={4}
                                    gridAutoFlow="row dense"
                                >
                                    {customerAddresses?.map((address, index) => (
                                        <AddressCard
                                            key={address.addressId}
                                            address={address}
                                            index={index}
                                            onEdit={handleEditAddress}
                                            onRemove={removeSavedAddress}
                                            isEditingAddress={isEditingAddress}
                                            selectedAddressId={selectedAddressId}
                                            type="shipping"
                                            EditFormComponent={
                                                <ShippingAddressForm
                                                    title={tShipping?.linkEditShipping}
                                                    hasSavedAddresses={hasSavedAddresses}
                                                    toggleAddressEdit={handleCloseForm}
                                                    form={form}
                                                    submitButtonLabel={submitButtonLabel}
                                                    formTitleAriaLabel={formTitleAriaLabel}
                                                    isNewAddress={isNewAddress}
                                                />
                                            }
                                        />
                                    ))}

                                    <AddNewAddressButton onClick={handleNewAddress}>
                                        {tShipping?.linkAddNewAddress}
                                    </AddNewAddressButton>
                                </SimpleGrid>
                            </RadioCardGroup>
                        )}
                    />
                )}

                {(customer?.isGuest || (isEditingAddress && !selectedAddressId)) && (
                    <ShippingAddressForm
                        title={tShipping?.titleAddNewAddress}
                        hasSavedAddresses={hasSavedAddresses}
                        toggleAddressEdit={handleCloseForm}
                        form={form}
                        submitButtonLabel={submitButtonLabel}
                        formTitleAriaLabel={formTitleAriaLabel}
                    />
                )}

                {customer?.isRegistered && !isEditingAddress && (
                    <Box pt={2}>
                        <Container variant="form">
                            <Button
                                type="submit"
                                width="full"
                                disabled={!form.formState.isValid || form.formState.isSubmitting}
                            >
                                {submitButtonLabel}
                            </Button>
                        </Container>
                    </Box>
                )}

                <PrivacyConsentText />
            </Stack>
        </form>
    )
}

ShippingAddressSelection.propTypes = {
    /** The form object returned from `useForm` */
    form: PropTypes.object,

    /** Optional address to use as default selection */
    selectedAddress: PropTypes.object,

    /** Override the submit button label */
    submitButtonLabel: PropTypes.string,

    /** aria label to use for the address group */
    formTitleAriaLabel: MESSAGE_PROPTYPE,

    /** Callback for form submit */
    onSubmit: PropTypes.func,

    /** Callback for address selection in radio buttons */
    onAddressSelect: PropTypes.func,

    /** Optional customer data to avoid duplicate API calls */
    customer: PropTypes.object
}

export default ShippingAddressSelection
